import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const preselectedEventId = searchParams.get('event');
  
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: '',
    organization: '',
    event: preselectedEventId || '',
    registrationType: 'attendee',
    specialRequirements: '',
    paymentMethod: 'credit',
    agreeToTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('registration'); // 'registration' or 'payment'
  const [error, setError] = useState('');

  // Fetch events from database
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data, error } = await supabase
          .from('events')
          .select('id, title, start_date, registration_fee, is_paid_event')
          .in('status', ['upcoming', 'confirmed', 'ongoing'])
          .order('start_date', { ascending: true });

        if (error) throw error;
        setEvents(data || []);
      } catch (err) {
        console.error('Error fetching events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchEvents();
  }, []);

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (user?.email && !formData.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate(`/login?redirect=/register${preselectedEventId ? `?event=${preselectedEventId}` : ''}`);
    }
  }, [user, navigate, preselectedEventId]);

  // Set preselected event when events load
  useEffect(() => {
    if (preselectedEventId && events.length > 0) {
      setFormData(prev => ({ ...prev, event: preselectedEventId }));
    }
  }, [preselectedEventId, events]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Check if already registered
      const { data: existing } = await supabase
        .from('event_registrations')
        .select('id')
        .eq('event_id', formData.event)
        .eq('attendee_email', formData.email.trim().toLowerCase())
        .single();

      if (existing) {
        setError('You are already registered for this event');
        setIsSubmitting(false);
        return;
      }

      // Create registration in database
      const { error: insertError } = await supabase
        .from('event_registrations')
        .insert({
          event_id: formData.event,
          user_id: user?.id || null,
          attendee_name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
          attendee_email: formData.email.trim().toLowerCase(),
          attendee_phone: formData.phone.trim() || null,
          notes: formData.specialRequirements.trim() || null,
          status: 'registered',
          payment_status: selectedEvent?.is_paid_event && selectedEvent?.registration_fee > 0 ? 'unpaid' : 'paid',
          amount_paid: 0
        });

      if (insertError) throw insertError;
      
      // Send confirmation email
      try {
        await fetch('/.netlify/functions/send-registration-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attendeeName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
            attendeeEmail: formData.email.trim().toLowerCase(),
            eventTitle: selectedEvent?.title,
            eventDate: selectedEvent?.start_date ? new Date(selectedEvent.start_date).toLocaleDateString() : null,
            eventLocation: null
          })
        });
      } catch (emailError) {
        console.error('Failed to send confirmation email:', emailError);
        // Don't fail registration if email fails
      }
      
      setSubmitSuccess(true);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('Failed to register. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get selected event details
  const selectedEvent = events.find(e => e.id === formData.event);

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-green-50 p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
            <p className="text-gray-600 mb-6">Thank you for registering for the event. We've sent a confirmation email to {formData.email}.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setSubmitSuccess(false)}
                className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 transition-colors"
              >
                Register Another Person
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Event Registration
          </h1>
          <p className="mt-3 text-xl text-gray-500">
            Secure your spot at our upcoming events
          </p>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {/* Progress Steps */}
          <div className="px-6 py-4 border-b border-gray-200">
            <nav className="flex items-center justify-center" aria-label="Progress">
              <ol className="flex items-center">
                <li className="relative pr-8 sm:pr-20">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className={`h-0.5 w-full ${activeTab === 'registration' ? 'bg-gray-200' : 'bg-indigo-600'}`}></div>
                  </div>
                  <button
                    onClick={() => setActiveTab('registration')}
                    className={`relative w-8 h-8 flex items-center justify-center rounded-full ${activeTab === 'registration' ? 'bg-indigo-600 text-white' : 'bg-white border-2 border-indigo-600 text-indigo-600'} hover:bg-indigo-50`}
                    aria-current={activeTab === 'registration' ? 'step' : undefined}
                  >
                    <span className="text-sm font-medium">1</span>
                  </button>
                  <span className="mt-2 block text-sm font-medium text-center text-gray-900">Registration</span>
                </li>
                <li className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className={`h-0.5 w-full ${activeTab === 'payment' ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
                  </div>
                  <button
                    onClick={() => activeTab === 'payment' && setActiveTab('payment')}
                    className={`relative w-8 h-8 flex items-center justify-center rounded-full ${activeTab === 'payment' ? 'bg-indigo-600 text-white' : 'bg-white border-2 border-gray-300 text-gray-500'} ${activeTab === 'payment' ? '' : 'cursor-not-allowed'}`}
                    disabled={activeTab !== 'payment'}
                    aria-current={activeTab === 'payment' ? 'step' : undefined}
                  >
                    <span className="text-sm font-medium">2</span>
                  </button>
                  <span className="mt-2 block text-sm font-medium text-center text-gray-500">Payment</span>
                </li>
              </ol>
            </nav>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            {activeTab === 'registration' ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div className="sm:col-span-3">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      First name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email address <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-6">
                    <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
                      Organization/Church
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="organization"
                        id="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-6">
                    <label htmlFor="event" className="block text-sm font-medium text-gray-700">
                      Select Event <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      {loadingEvents ? (
                        <div className="flex items-center text-gray-500 text-sm py-2">
                          <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading events...
                        </div>
                      ) : events.length === 0 ? (
                        <p className="text-gray-500 text-sm py-2">No upcoming events available for registration.</p>
                      ) : (
                        <select
                          id="event"
                          name="event"
                          required
                          value={formData.event}
                          onChange={handleChange}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        >
                          <option value="">Select an event</option>
                          {events.map((event) => (
                            <option key={event.id} value={event.id}>
                              {event.title} - {new Date(event.start_date).toLocaleDateString()}
                              {event.is_paid_event && event.registration_fee ? ` ($${event.registration_fee})` : ' (Free)'}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  {/* Show selected event pricing info */}
                  {selectedEvent && (
                    <div className="sm:col-span-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900">{selectedEvent.title}</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Date: {new Date(selectedEvent.start_date).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-blue-700">
                          Registration Fee: {selectedEvent.is_paid_event && selectedEvent.registration_fee 
                            ? `$${selectedEvent.registration_fee.toFixed(2)}` 
                            : 'Free'}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="sm:col-span-6">
                    <label htmlFor="specialRequirements" className="block text-sm font-medium text-gray-700">
                      Special Requirements
                    </label>
                    <div className="mt-1">
                      <textarea
                        id="specialRequirements"
                        name="specialRequirements"
                        rows={3}
                        value={formData.specialRequirements}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="Dietary restrictions, accessibility needs, etc."
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="agreeToTerms"
                      name="agreeToTerms"
                      type="checkbox"
                      required
                      checked={formData.agreeToTerms}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="agreeToTerms" className="font-medium text-gray-700">
                      I agree to the <a href="/terms" className="text-indigo-600 hover:text-indigo-500">Terms of Service</a> and{' '}
                      <a href="/privacy" className="text-indigo-600 hover:text-indigo-500">Privacy Policy</a>
                      <span className="text-red-500"> *</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end">
                  {selectedEvent?.is_paid_event && selectedEvent?.registration_fee > 0 ? (
                    <button
                      type="button"
                      onClick={() => setActiveTab('payment')}
                      className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Continue to Payment
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.event}
                      className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isSubmitting ? 'Registering...' : 'Complete Registration (Free)'}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Event</span>
                      <span className="font-medium">{selectedEvent?.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Registration Fee</span>
                      <span className="font-medium">
                        {selectedEvent?.registration_fee ? `$${selectedEvent.registration_fee.toFixed(2)}` : 'Free'}
                      </span>
                    </div>
                    <div className="border-t border-gray-200 pt-2">
                      <div className="flex justify-between font-medium text-gray-900">
                        <span>Total</span>
                        <span>{selectedEvent?.registration_fee ? `$${selectedEvent.registration_fee.toFixed(2)}` : 'Free'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Payment Method</h3>
                  <div className="space-y-2">
                    {[
                      { id: 'credit', label: 'Credit/Debit Card' },
                      { id: 'paypal', label: 'PayPal' },
                      { id: 'bank', label: 'Bank Transfer' },
                    ].map((method) => (
                      <div key={method.id} className="flex items-center">
                        <input
                          id={method.id}
                          name="paymentMethod"
                          type="radio"
                          value={method.id}
                          checked={formData.paymentMethod === method.id}
                          onChange={handleChange}
                          className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                        />
                        <label htmlFor={method.id} className="ml-3 block text-sm font-medium text-gray-700">
                          {method.label}
                        </label>
                      </div>
                    ))}
                  </div>

                  {formData.paymentMethod === 'credit' && (
                    <div className="mt-4 space-y-4">
                      <div>
                            <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700">
                              Card number <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                id="cardNumber"
                                name="cardNumber"
                                placeholder="0000 0000 0000 0000"
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                required
                              />
                            </div>
                          </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700">
                            Expiry date <span className="text-red-500">*</span>
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              id="expiryDate"
                              name="expiryDate"
                              placeholder="MM/YY"
                              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor="cvv" className="block text-sm font-medium text-gray-700">
                            CVV <span className="text-red-500">*</span>
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              id="cvv"
                              name="cvv"
                              placeholder="123"
                              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="cardName" className="block text-sm font-medium text-gray-700">
                          Name on card <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="cardName"
                            name="cardName"
                            placeholder="John Smith"
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.paymentMethod === 'paypal' && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md text-center">
                      <p className="text-sm text-yellow-700">You will be redirected to PayPal to complete your payment.</p>
                    </div>
                  )}

                  {formData.paymentMethod === 'bank' && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <h4 className="text-sm font-medium text-blue-800">Bank Transfer Details</h4>
                      <div className="mt-2 text-sm text-blue-700 space-y-1">
                        <p>Bank Name: Conference Bank</p>
                        <p>Account Name: Event Management Inc</p>
                        <p>Account Number: 1234 5678 9012 3456</p>
                        <p>SWIFT/BIC: CONFUS33</p>
                        <p className="mt-2 text-blue-800 font-medium">Please use your full name as the payment reference.</p>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-200 mt-6">
                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={() => setActiveTab('registration')}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          'Complete Registration'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
